import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { LANDING } from "@/constants/testIds";

const NAV_ITEMS = [
  { label: "Home", id: "home", to: "/" },
  {
    label: "Strategies",
    id: "strategies",
    to: "/strategies",
    children: [
      { label: "Industrial Credit", to: "/strategies/industrial-credit" },
      { label: "Bridge Capital & Special Situations", to: "/strategies/bridge-capital" },
      { label: "Opportunistic Industrial Equity", to: "/strategies/opportunistic-equity" },
      { label: "Structured Capital Solutions", to: "/strategies/structured-solutions" },
      { label: "Selective Capital Pathways", to: "/capital-pathways", divider: true },
      {
        label: "Family Office Investment Solutions",
        to: "/family-office-investment-solutions",
        testId: "kr-nav-family-office",
      },
    ],
  },
  { label: "Funds", id: "funds", to: "/funds" },
  {
    label: "Perspectives",
    id: "perspectives",
    to: "/perspectives",
    children: [
      { label: "The Industrial Capital Cycle", to: "/perspectives/industrial-capital-cycle" },
      { label: "Senior-Secured by Design", to: "/perspectives/senior-secured-by-design" },
      { label: "Pre-Event Bridge Financings", to: "/perspectives/pre-event-bridge-financings" },
      { label: "Reshoring, Logistics & the Asset-Backed Premium", to: "/perspectives/reshoring-logistics-asset-backed-premium" },
    ],
  },
  { label: "Team", id: "team", to: "/team" },
  { label: "Responsible Capital", id: "responsible", to: "/responsible-capital" },
  { label: "Newsroom", id: "news", to: "/news" },
  { label: "Contact", id: "contact", to: "/contact" },
];

/** Desktop nav link — supports optional hover dropdown */
const DesktopNavItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (item.children) setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const linkCls =
    "font-serif-display text-[14px] xl:text-[15px] text-[var(--kr-text)]/85 hover:text-[var(--kr-gold)] transition-colors duration-300 inline-flex items-center gap-1.5 py-2";

  if (!item.children) {
    return (
      <Link
        to={item.to}
        data-testid={`${LANDING.navLinkPrefix}${item.id}`}
        className={linkCls}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      <Link
        to={item.to}
        data-testid={`${LANDING.navLinkPrefix}${item.id}`}
        aria-haspopup="true"
        aria-expanded={open}
        className={linkCls}
      >
        {item.label}
        <span
          aria-hidden
          className={`text-[9px] text-[var(--kr-gold)]/70 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </Link>

      <div
        data-testid={`${LANDING.navDropdownPrefix}${item.id}`}
        className={`absolute left-0 top-full pt-3 min-w-[300px] transition-all duration-300 ${
          open
            ? "opacity-100 visible translate-y-0"
            : "opacity-0 invisible -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="bg-[rgba(10,13,18,0.97)] backdrop-blur-xl border border-[var(--kr-line-soft)] py-3">
          <ul role="menu">
            {item.children.map((c) => (
              <li key={c.to} role="none" className={c.divider ? "mt-2 pt-3 border-t border-[var(--kr-line-soft)]" : ""}>
                <Link
                  to={c.to}
                  role="menuitem"
                  data-testid={c.testId}
                  className="block px-5 py-2.5 text-[13px] text-[var(--kr-text)]/85 hover:text-[var(--kr-gold)] hover:bg-[rgba(201,169,110,0.04)] font-serif-display transition-colors"
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Navbar = ({ onRequestAccess }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={LANDING.navbar}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(10,13,18,0.82)] backdrop-blur-xl border-b border-[var(--kr-line-soft)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-[88px]">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            {NAV_ITEMS.map((n) => (
              <DesktopNavItem key={n.id} item={n} />
            ))}
          </nav>

          <button
            type="button"
            onClick={onRequestAccess}
            data-testid={LANDING.requestAccessNav}
            className="hidden md:inline-flex items-center gap-3 text-[11px] tracking-[0.22em] font-mono-label text-[var(--kr-gold)] pb-1 border-b border-[var(--kr-gold)] hover:opacity-80 transition-opacity"
          >
            REQUEST ACCESS
          </button>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid={LANDING.mobileNavToggle}
            className="lg:hidden text-[var(--kr-text)] p-2"
            aria-label="Toggle navigation"
          >
            <span className="block w-6 h-px bg-current mb-[6px]" />
            <span className="block w-6 h-px bg-current mb-[6px]" />
            <span className="block w-4 h-px bg-current ml-auto" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ${
          mobileOpen ? "max-h-[860px]" : "max-h-0"
        } bg-[rgba(10,13,18,0.96)] backdrop-blur-xl border-t border-[var(--kr-line-soft)]`}
      >
        <div className="px-6 py-8 flex flex-col gap-4">
          {NAV_ITEMS.map((n) => {
            const parentCls = "font-serif-display text-2xl text-[var(--kr-text)] hover:text-[var(--kr-gold)] transition-colors flex items-center justify-between";
            if (!n.children) {
              return (
                <Link
                  key={n.id}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className={parentCls}
                >
                  {n.label}
                </Link>
              );
            }
            const expanded = !!mobileExpanded[n.id];
            return (
              <div key={n.id}>
                <button
                  type="button"
                  onClick={() => setMobileExpanded((s) => ({ ...s, [n.id]: !s[n.id] }))}
                  className={`${parentCls} w-full text-left`}
                  aria-expanded={expanded}
                >
                  <span>{n.label}</span>
                  <span aria-hidden className={`text-[14px] text-[var(--kr-gold)]/70 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}>▾</span>
                </button>
                <div className={`overflow-hidden transition-[max-height] duration-400 ${expanded ? "max-h-[400px]" : "max-h-0"}`}>
                  <ul className="pl-4 pt-3 pb-1 space-y-3 border-l border-[var(--kr-line-soft)] ml-1">
                    <li>
                      <Link
                        to={n.to}
                        onClick={() => setMobileOpen(false)}
                        className="block font-mono-label text-[11px] text-[var(--kr-gold)]"
                      >
                        ALL {n.label.toUpperCase()} →
                      </Link>
                    </li>
                    {n.children.map((c) => (
                      <li key={c.to} className={c.divider ? "pt-2 mt-2 border-t border-[var(--kr-line-soft)]" : ""}>
                        <Link
                          to={c.to}
                          onClick={() => setMobileOpen(false)}
                          data-testid={c.testId ? `${c.testId}-mobile` : undefined}
                          className="block text-[16px] text-[var(--kr-text)]/85 font-serif-display hover:text-[var(--kr-gold)]"
                        >
                          {c.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setMobileOpen(false);
              onRequestAccess();
            }}
            className="mt-4 self-start kr-btn-primary"
          >
            Request Access
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
